<template>
  <div>
    <my-header></my-header>

    <div
      v-if="isLoadedNormal"
      class="content-container-level-1"
    >
      <h2>New season</h2>

      <form @submit.prevent="createSeasonWithConfirmation">

        <h5>Type</h5>
        <div class="form-group">
          <b-form-select
            v-model="type"
            :options="typeOptions"
          />
        </div>

        <hr>

        <h5>Segment</h5>
        <div class="form-group">
          <b-form-select
            id="player-group-selector"
            v-model="playerGroupId"
            :options="playerGroupOptions"
          />
        </div>

        <hr>

        <h5>Period</h5>
        <table class="period-table">
          <tr>
            <td>Start</td>
            <td class="date">
              <b-form-datepicker
                v-model="plannedStartDate"
                :min="minPlannedStartDate"
                :start-weekday="MONDAY"
              />
            </td>
            <td>
              <time-input v-model="plannedStartTime"/>
            </td>
          </tr>
          <tr>
            <td>Finish</td>
            <td class="date">
              <b-form-datepicker
                v-model="plannedFinishDate"
                :min="minPlannedFinishDate"
                :start-weekday="MONDAY"
              />
            </td>
            <td>
              <time-input v-model="plannedFinishTime"/>
            </td>
          </tr>
          <tr>
            <td>Duration</td>
            <td colspan="2" class="pl-3">{{durationStr}}</td>
          </tr>
        </table>

        <hr>

        <h5>Client min version</h5>
        <client-version
          :clientVersionInfo="clientVersionInfo"
          class="mb-2"
        />

        <p class="mb-2">
          {{clientMinVersionComment}}
        </p>
        <input class="form-control" v-model="clientMinVersion" required>

        <hr>

        <b-button :to="{name: 'seasons'}">Cancel</b-button>
        <b-button type="submit" variant="primary">Create</b-button>
      </form>
    </div>

    <loading-indicator
      v-if="isLoading"
    />
  </div>
</template>

<script>
/**
 * @typedef { import('../service/confirm.js').ConfirmHandle } ConfirmHandle
 */
import { confirmAction } from '../service/confirm.js';

/**
 * @typedef { import('../model/event.js').PlayerGroupId } PlayerGroupId
 */
import { DEFAULT_PLAYER_GROUP_ID      } from '../model/event.js';
import { getPlayerGroupOptionsVerbose } from '../model/event.js';

import { ClientVersionInfo                   } from '../model/version.js';

import { floorMomentTo5Min                   } from '../util.js';
import { convertMomentToDataStr              } from '../util.js';
import { convertMomentToTimeStr              } from '../util.js';
import { getDurationStrBetweenMoments        } from '../util.js';
import { convertDataStrAndTimeStrToLocalData } from '../util.js';
import { normalizeVersionStr                 } from '../util.js';

import notifier from '../notifier.js';
import { HTTP } from '../http-common.js';

import MyHeader from './Header.vue';
import LoadingIndicator from './LoadingIndicator.vue';
import ClientVersion from './ClientVersion.vue';
import TimeInput from './TimeInput.vue';

const LOAD_STATE_INIT          = 1;
const LOAD_STATE_LOADING       = 2;
const LOAD_STATE_LOADED_NORMAL = 3;
const LOAD_STATE_LOADED_ERROR  = 4;

export default {
  name: 'SeasonAdd',

  components: {
    MyHeader,
    LoadingIndicator,
    ClientVersion,
    TimeInput,
  },

  data() {
    const now = new Date();

    const start = floorMomentTo5Min(now);
    const startDateStr = convertMomentToDataStr(start);
    const startTimeStr = convertMomentToTimeStr(start);

    // By default season duration is 48 hours (2 days).
    const DEFAULT_DURATION_HOURS = 48;

    const finish = new Date(start.getTime());
    finish.setHours(finish.getHours() + DEFAULT_DURATION_HOURS);
    const finishDateStr = convertMomentToDataStr(finish);
    const finishTimeStr = convertMomentToTimeStr(finish);

    return {
      MONDAY: 1,

      loadState: LOAD_STATE_INIT,

      /** @type {?ClientVersionInfo} */
      clientVersionInfo: null,

      /** @type {?number} */
      type: null,

      /** @type {?SeasonTypeOption[]} */
      typeOptions: null,

      plannedStartDate:     startDateStr,
      minPlannedStartDate:  startDateStr,
      plannedStartTime:     startTimeStr,

      plannedFinishDate:    finishDateStr,
      minPlannedFinishDate: startDateStr,
      plannedFinishTime:    finishTimeStr,

      clientMinVersion: '',

      playerGroupId:      DEFAULT_PLAYER_GROUP_ID,
      playerGroupOptions: getPlayerGroupOptionsVerbose(),
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadState === LOAD_STATE_LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadState === LOAD_STATE_LOADED_NORMAL;
    },

    /** @returns {?Date} */
    plannedStartMoment() {
      return convertDataStrAndTimeStrToLocalData(this.plannedStartDate, this.plannedStartTime);
    },

    /** @returns {?number} */
    plannedStartSec() {
      const plannedStartMoment = this.plannedStartMoment;
      if(plannedStartMoment === null) {
        return null;
      }

      return Math.floor(plannedStartMoment.getTime() / 1000);
    },

    /** @returns {?Date} */
    plannedFinishMoment() {
      return convertDataStrAndTimeStrToLocalData(this.plannedFinishDate, this.plannedFinishTime);
    },

    /** @returns {?number} */
    plannedFinishSec() {
      const plannedFinishMoment = this.plannedFinishMoment;
      if(plannedFinishMoment === null) {
        return null;
      }

      return Math.floor(plannedFinishMoment.getTime() / 1000);
    },

    /** @returns {?string} */
    durationStr() {
      const plannedStartMoment = this.plannedStartMoment;
      if(plannedStartMoment === null) {
        return null;
      }

      const plannedFinishMoment = this.plannedFinishMoment;
      if(plannedFinishMoment === null) {
        return null;
      }

      return getDurationStrBetweenMoments(plannedStartMoment, plannedFinishMoment);
    },

    /** @returns {?string} */
    clientMinVersionComment() {
      const clientVersionInfo = this.clientVersionInfo;
      if(clientVersionInfo === null) {
        return null;
      }

      if(clientVersionInfo.gameVersion === clientVersionInfo.forcedVersion) {
        let message = '';
        message += 'Client min version of new season must be inside this range. ';
        message += 'Server version and forced version are equal. '
        message += 'Consequently, value ' + clientVersionInfo.gameVersion + ' is the only option for min client version.';
        return message;
      }

      return 'Specify client min version of new season inside this range.';
    },
  },

  created() {
    this.loadNewSeasonInfo();
  },

  methods: {
    loadNewSeasonInfo() {
      notifier.deleteSenderNotifications(this);

      this.loadState = LOAD_STATE_LOADING;

      HTTP.get('/new_season_info')
      .then(response => {
        /** @type {NewSeasonInfo} */
        const data = response.data.data;

        const gameVersion = normalizeVersionStr(data.gameVersion);
        if(gameVersion === null) {
          notifier.showError('Loaded gameVersion is invalid.', {sender: this});
          return;
        }

        const forcedVersion = data.forcedVersion;
        this.clientVersionInfo = new ClientVersionInfo(gameVersion, forcedVersion);

        this.typeOptions = getTypeOptions(data.seasonTypes);
        if(this.typeOptions.length > 0) {
          this.type = this.typeOptions[0].value;
        }

        this.clientMinVersion = forcedVersion;

        this.loadState = LOAD_STATE_LOADED_NORMAL;
      })
      .catch(err => {
        this.loadState = LOAD_STATE_LOADED_ERROR;
        notifier.showError(err.response.data, {sender: this});
      });
    },

    createSeasonWithConfirmation() {
      notifier.deleteSenderNotifications(this);

      const seasonDefInputReport = this.getSeasonDefInputReport();

      if(seasonDefInputReport.error !== null) {
        notifier.showError(seasonDefInputReport.error.message, {sender: this});
        return;
      }

      const seasonDef = seasonDefInputReport.seasonDef;
      if(seasonDef === null) {
        notifier.showError('Invalid input report. seasonDef === null.', {sender: this});
        return;
      }

      this.confirmSeasonCreation(() => {
        this.createSeason(seasonDef);
      });
    },

    getSeasonDefInputReport() {
      const plannedStartSec = this.plannedStartSec;
      if(plannedStartSec === null) {
        return new SeasonDefInputReport(
          null,
          new Error('Start time must be specified.')
        );
      }

      const plannedFinishSec = this.plannedFinishSec;
      if(plannedFinishSec === null) {
        return new SeasonDefInputReport(
          null,
          new Error('Finish time must be specified.')
        );
      }

      if(plannedFinishSec === plannedStartSec) {
        return new SeasonDefInputReport(
          null,
          new Error('Season duration is zero.')
        );
      }

      if(plannedFinishSec < plannedStartSec) {
        return new SeasonDefInputReport(
          null,
          new Error('Finish time must be after start time.')
        );
      }

      const trimmedClientMinVersion = this.clientMinVersion.trim();
      if(trimmedClientMinVersion.length === 0) {
        return new SeasonDefInputReport(
          null,
          new Error('Client min version must be specified.')
        );
      }

      const seasonProtoId = this.type;
      if(seasonProtoId === null) {
        return new SeasonDefInputReport(
          null,
          new Error('Season proto must be specified.')
        );
      }

      const seasonDef = new SeasonDef(
        seasonProtoId,
        plannedStartSec,
        plannedFinishSec,
        trimmedClientMinVersion,
        this.playerGroupId,
      );
      return new SeasonDefInputReport(seasonDef, null);
    },

    /**
     * @param {ConfirmHandle} onConfirmed
     */
    confirmSeasonCreation(onConfirmed) {
      confirmAction(
        this,
        'Are you sure you want to create this season {env}?',
        'Create',
        onConfirmed,
      );
    },

    /**
     * @param {SeasonDef} seasonDef
     */
    createSeason(seasonDef) {
      const data = new FormData();
      data.set('proto_id',           seasonDef.type.toString());
      data.set('stime',              seasonDef.plannedStartSec.toString());
      data.set('ftime',              seasonDef.plannedFinishSec.toString());
      data.set('client_min_version', seasonDef.clientMinVersion);
      data.set('player_group_id',    seasonDef.playerGroupId.toString());

      HTTP.post('/season/add', data)
      .then(response => {
        const result = response.data.result;

        const RESULT_SUCCESS = 1;
        if(result.code === RESULT_SUCCESS) {
          notifier.showSuccessAfterTransition('Season created.');
          this.$router.push({name: 'seasons'});
          return;
        }

        notifier.showError(result.message, {sender: this});
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },
  },
};

/**
 * @typedef {Object} NewSeasonInfo
 * @property {SeasonType[]} seasonTypes
 * @property {string} gameVersion
 * @property {string} forcedVersion
 * 
 * @typedef {Object} SeasonType
 * @property {number} id
 * @property {string} name
 */

/**
 * @typedef {Object} SeasonTypeOption
 * @property {number} value
 * @property {string} text
 */

/**
 * Options for component b-form-select.
 * @param {SeasonType[]} typeList
 * @returns {SeasonTypeOption[]}
 */
function getTypeOptions(typeList) {
  const options = [];

  for(const t of typeList) {
    const option = {
      value: t.id,
      text: t.name,
    };
    options.push(option);
  }

  return options;
}

class SeasonDefInputReport {
  /**
   * @param {?SeasonDef} seasonDef
   * @param {?Error}     error
   */
  constructor(seasonDef, error) {
    this.seasonDef = seasonDef;
    this.error     = error;
  }
}

class SeasonDef {
  /**
   * @param {number}        type
   * @param {number}        plannedStartSec
   * @param {number}        plannedFinishSec
   * @param {string}        clientMinVersion
   * @param {PlayerGroupId} playerGroupId
   */
  constructor(type, plannedStartSec, plannedFinishSec, clientMinVersion, playerGroupId) {
    this.type              = type;
    this.plannedStartSec   = plannedStartSec;
    this.plannedFinishSec  = plannedFinishSec;
    this.clientMinVersion  = clientMinVersion;
    this.playerGroupId     = playerGroupId;
  }
}
</script>

<style scoped>
.period-table {
  width: 100%;
}

.period-table td:first-child {
  width: 1%;
  padding-right: 0.4em;
}

.period-table td.date {
  padding-right: 0.19em;
}

.period-table > tr:first-child > td,
.period-table > tr:nth-child(2) > td {
  padding-bottom: 0.4em;
}

.client-min-version-range td:first-child {
  padding-right: 0.3rem;
}
</style>
